<template>
  <div>
    <b-card>
      <div class="organism-repository_header card-padding">
        <span class="header__label">Repository Management</span>
        <atom-button
          v-if="hasUpdatePermission"
          buttonId="create-new-repository"
          class="mt-1 mt-md-0 ml-md-2"
          buttonStyle="border"
          :isDisable="!currentVeeamId || !getVeeamStatus"
          @click="clickCreateRepository"
        >
          <div class="d-flex d-md-none">Create</div>
          <div class="d-none d-md-flex">Create New Repository</div>
        </atom-button>
      </div>
      <div class="filter-container card-padding pt-0 pb-3">
        <div class="row mt-0">
          <div
            class="col-12 col-md-4 mb-1 mb-md-0 d-flex justify-content-between"
          >
            <atom-input
              v-model="filterList.name"
              title="Name"
              placeholder="Name"
              :isDisable="!currentVeeamId"
            ></atom-input>
          </div>
          <div
            class="col-12 col-md-5 mb-1 mb-md-0 d-flex justify-content-between"
          >
            <atom-select
              v-model="filterList.type"
              title="Type"
              placeholder="Type"
              label="type"
              :isClearable="true"
              :isAppendToBody="false"
              :isEnableProper="false"
              :option="typeList"
              :isDisable="!currentVeeamId"
            ></atom-select>
            <div class="d-flex align-items-end">
              <atom-button
                buttonId="button-search"
                iconLeft="SearchIcon"
                :isDisable="!currentVeeamId || !getVeeamStatus"
                @click="clickButtonSearch"
                class="search__button ml-2"
              ></atom-button>
              <div
                class="reset-filter-button ml-1"
                :class="{ 'disabled-button': !currentVeeamId }"
                @click="clickButtonClearFilter"
              >
                <span>Reset</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12">
          <b-table
            sticky-header
            responsive
            :items="items"
            :fields="hasUpdatePermission ? fields : fields.filter(field => field.key !== 'deleteIcon')"
            :hover="true"
            @row-clicked="selectRow"
          >
            <template #cell(repository)="data">
              <div>
                <b>{{ data.value.name }}</b>
              </div>
              <div class="sub-header">{{ data.value.ip }}</div>
            </template>
            <template #cell(type)="data">
              <div>{{ data.value }}</div>
            </template>
            <template #cell(path)="data">
              <div>{{ data.value }}</div>
            </template>
            <template #cell(capacity)="data">
              <div>{{ data.value }}</div>
            </template>
            <template #cell(freeSpace)="data">
              <div>
                <b>{{ data.value }}</b>
              </div>
            </template>
            <template #cell(deleteIcon)="data">
              <div :id="'button-delete-repository' + data.item.id">
                <atom-button
                  buttonId="button-delete"
                  iconLeft="Trash2Icon"
                  buttonStyle="flat"
                  buttonColor="secondary"
                  :isDisable="!data.item.isDelete"
                  @click="clickDeleteRepository(data)"
                ></atom-button>
              </div>
              <b-tooltip
                v-if="!data.item.isDelete"
                :target="'button-delete-repository' + data.item.id"
                placement="left"
                boundary="window"
                variant="danger"
              >
                Repository is used for Veeam backup and can't be delete
              </b-tooltip>
            </template>
          </b-table>
        </div>
        <div
          v-if="!currentVeeamId"
          class="w-100 d-flex flex-column justify-content-center align-items-center p-4"
        >
          <feather-icon
            icon="CloudOffIcon"
            class="empty-data__icon"
          ></feather-icon>
          <label class="empty-data__text--title">No Repository</label>
          <label v-if="!currentVeeamId" class="empty-data__text--body"
            >Please Select Veeam</label
          >
        </div>
      </div>
      <div class="row card-padding">
        <div class="col-6">Showing {{ items.length }} entries</div>
        <div
          v-if="filterList.totalCount"
          class="col-6 d-flex justify-content-end"
        >
          <b-pagination
            v-model="filterList.currentPage"
            :total-rows="filterList.totalCount"
            :per-page="perPage"
            size="sm"
            class="my-0"
          ></b-pagination>
        </div>
      </div>
    </b-card>
    <section name="create-repository">
      <molecule-modal-dialog
        modalId="create-repository"
        :isTextCenter="true"
        buttonTextLeft="Create Repository"
        textTitle="Create Repository"
        :isSlotBody="true"
        :isCloseOnBackDrop="false"
        @clickButtonLeft="clickConfirmCreateRepository"
        @clickButtonRight="clickCancelCreateRepository"
        ><template #body
          ><div class="w-100">
            <div class="row mt-0">
              <div class="col-12 col-md-6 mt-2">
                <atom-input
                  v-model="repository.name"
                  title="Repository Name"
                  placeholder="Repository Name"
                  :isAsterisk="true"
                  :isError="isErrorName"
                  errorMessage="Name is required"
                ></atom-input>
              </div>
              <div class="col-12 col-md-6 mt-2 pl-md-0">
                <atom-select
                  v-model="repository.server"
                  title="Repository Server"
                  placeholder="Repository Server"
                  label="server"
                  :isAsterisk="true"
                  :isClearable="false"
                  :isAppendToBody="false"
                  :isEnableProper="false"
                  :isError="isErrorServer"
                  :option="serverList"
                  errorMessage="Repository is required"
                ></atom-select>
              </div>
              <div class="col-12 mt-2">
                <atom-text-area
                  v-model="repository.description"
                  title="Description"
                  placeholder="Description"
                  :rows="2"
                  :isAsterisk="true"
                  :isError="isErrorDescription"
                  errorMessage="Description is required"
                ></atom-text-area>
              </div>
              <div class="col-12 col-md-6 mt-2">
                <atom-input
                  v-model="repository.path"
                  title="Path to backup server"
                  placeholder="Path to backup server"
                  :isAsterisk="true"
                  :isError="isErrorPath"
                  errorMessage="Path to backup server is required"
                ></atom-input>
              </div>
              <div class="col-12 col-md-3 mt-2 pl-md-0">
                <atom-input
                  v-model="repository.limitTask"
                  title="Limit Concurrent Task"
                  placeholder="Limit"
                  :isAsterisk="true"
                  :isError="isErrorLimitTask"
                  :errorMessage="errorMessageForLimitTask"
                ></atom-input>
              </div>
              <div class="col-12 col-md-3 mt-2 pl-md-0">
                <atom-input
                  v-model="repository.readWriteRate"
                  title="R/W Limit (MB/s)"
                  placeholder="max"
                  :isError="isErrorReadWriteRate"
                  errorMessage="R/W Rate should be a number"
                ></atom-input>
              </div>
            </div>
          </div> </template
      ></molecule-modal-dialog>
    </section>
    <section name="edit-repository">
      <molecule-modal-dialog
        modalId="edit-repository"
        :isTextCenter="true"
        textTitle="Repository Detail"
        buttonTextLeft="Save changes"
        :isSlotBody="true"
        :isCloseOnBackDrop="false"
        :isDisableButtonLeft="!isEdit || isError"
        :isButtonLeft="hasUpdatePermission"
        :isButtonRight="hasUpdatePermission"
        @clickButtonLeft="clickConfirmEditRepository"
        @clickButtonRight="clickCancelEditRepository"
        ><template #body
          ><div class="w-100">
            <div class="row mt-0">
              <div class="col-12 col-md-6 mt-2">
                <atom-input
                  v-model="repository.name"
                  title="Repository Name"
                  placeholder="Repository Name"
                  :isError="isErrorName"
                  :isAsterisk="true"
                  errorMessage="Name is required"
                  :isDisable="!hasUpdatePermission"
                ></atom-input>
              </div>
              <div class="col-12 col-md-6 mt-2 pl-md-0">
                <atom-input
                  v-model="repository.server"
                  title="Repository Server"
                  placeholder="Repository Server"
                  :isDisable="true"
                  :isAsterisk="true"
                  :option="serverList"
                ></atom-input>
              </div>
              <div class="col-12 mt-2">
                <atom-text-area
                  v-model="repository.description"
                  title="Description"
                  placeholder="Description"
                  :rows="2"
                  :isError="isErrorDescription"
                  :isAsterisk="true"
                  errorMessage="Description is required"
                  :isDisable="!hasUpdatePermission"
                ></atom-text-area>
              </div>
              <div class="col-12 col-md-6 mt-2">
                <atom-input
                  v-model="repository.path"
                  title="Path to backup server"
                  placeholder="Path to backup server"
                  :isError="isErrorPath"
                  :isAsterisk="true"
                  errorMessage="Path to backup server is required"
                  :isDisable="!hasUpdatePermission"
                ></atom-input>
              </div>
              <div class="col-12 col-md-3 mt-2 pl-md-0">
                <atom-input
                  v-model="repository.limitTask"
                  title="Limit Concurrent Task"
                  placeholder="Limit"
                  :isError="isErrorLimitTask"
                  :isAsterisk="true"
                  :errorMessage="errorMessageForLimitTask"
                  :isDisable="!hasUpdatePermission"
                ></atom-input>
              </div>
              <div class="col-12 col-md-3 mt-2 pl-md-0">
                <atom-input
                  v-model="repository.readWriteRate"
                  title="R/W Limit (MB/s)"
                  placeholder="max"
                  :isError="isErrorReadWriteRate"
                  errorMessage="R/W should be a number"
                  :isDisable="!hasUpdatePermission"
                ></atom-input>
              </div>
            </div>
          </div> </template
      ></molecule-modal-dialog>
    </section>
    <section name="delete-repository">
      <molecule-modal-dialog
        modalId="delete-repository"
        :isTextCenter="true"
        icon="AlertCircleIcon"
        textTitle="Do you want to delete?"
        buttonTextLeft="Delete"
        buttonColorLeft="danger"
        :isDisableButtonLeft="false"
        :isSlotBody="true"
        @clickButtonLeft="confirmDelete"
        ><template #body
          ><div class="w-70 pl-3 pl-sm-4">
            <div>
              <label class="width-85px">Repository:</label>
              <b>{{ deleteMessage.repository }}</b>
            </div>
            <div>
              <label class="width-85px">Server IP: </label
              ><b>{{ deleteMessage.serverIp }}</b>
            </div>
          </div>
        </template>
      </molecule-modal-dialog>
    </section>
  </div>
</template>

<script>
import { BCard, BTable, BPagination, BTooltip } from "bootstrap-vue"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomSelect from "@/components/atoms/AtomSelect.vue"
import AtomTextArea from "@/components/atoms/AtomTextArea.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import mixinsValidator from "@/mixins/mixinsValidator"
import { mapGetters } from "vuex"
import { PermissionUser } from "@/constants/permission-user"
import mixinsUser from "@/mixins/mixinsUser"

export default {
  name: "OrganismRepository",
  mixins: [mixinsValidator, mixinsUser],
  components: {
    BCard,
    BTable,
    BPagination,
    BTooltip,
    AtomButton,
    AtomInput,
    AtomSelect,
    AtomTextArea,
    MoleculeModalDialog,
  },
  props: {
    filterList: {
      required: true,
      type: Object,
    },
    currentVeeamId: {
      type: String,
      required: true,
    },
    fields: {
      type: Array,
      required: false,
      default: [],
    },
    items: {
      type: Array,
      required: false,
      default: [],
    },
    serverList: {
      type: Array,
      required: false,
    },
    mountServer: {
      type: Object,
      required: true,
    },
    currentRepository: {
      type: [Object, null],
      required: false,
      default: null,
    },
  },
  emits: [
    "clickButtonSearch",
    "selectRow",
    "createRepository",
    "updateRepository",
    "deleteRepository",
  ],
  data() {
    return {
      isValidateTrigger: false,
      perPage: 10,
      repository: {
        name: "",
        server: "",
        description: "",
        path: "",
        limitTask: 4,
        readWriteRate: 0,
        mountServer: "C:\\ProgramData\\Veeam\\Backup\\IRCache\\",
        cacheRecoveryPath: "",
      },
      deleteMessage: {
        repository: "",
        serverIp: "",
      },
      typeList: ["WinLocal", "LinuxLocal"],
    }
  },
  computed: {
    ...mapGetters("app", ["getVeeamStatus"]),

    hasUpdatePermission() {
      return this.hasPermission(PermissionUser.BMM.REPOSITORY_WRITE)
    },

    isEdit() {
      return (
        JSON.stringify(this.repository) !==
        JSON.stringify(this.currentRepository)
      )
    },

    isError() {
      return (
        this.isErrorName ||
        this.isErrorServer ||
        this.isErrorDescription ||
        this.isErrorPath ||
        this.isErrorLimitTask ||
        this.isErrorReadWriteRate
      )
    },
    isErrorName() {
      return this.isValidateTrigger
        ? this.isEmptyString(this.repository.name)
        : false
    },
    isErrorServer() {
      return this.isValidateTrigger
        ? this.isEmptyObject(this.repository.server)
        : false
    },
    isErrorDescription() {
      return this.isValidateTrigger
        ? this.isEmptyString(this.repository.description)
        : false
    },
    isErrorPath() {
      return this.isValidateTrigger
        ? this.isEmptyString(this.repository.path)
        : false
    },
    isErrorMountServer() {
      return this.isValidateTrigger
        ? this.isEmptyObject(this.repository.mountServer)
        : false
    },
    isErrorLimitTask() {
      return this.isValidateTrigger
        ? this.isNotNumeric(this.repository.limitTask)
        : false
    },
    isErrorReadWriteRate() {
      return this.isValidateTrigger
        ? this.isNotNumeric(this.repository.readWriteRate) &&
            this.hasString(this.repository.readWriteRate)
        : false
    },
    isErrorCacheRecoveryPath() {
      return this.isValidateTrigger
        ? this.isEmptyObject(this.repository.cacheRecoveryPath)
        : false
    },
    errorMessageForLimitTask() {
      if (this.repository.limitTask === "") return "Limit tasks is required"
      else {
        return "Limit task should be a number"
      }
    },
  },
  watch: {
    currentRepository: {
      deep: true,
      handler() {
        if (this.currentRepository) {
          this.repository = JSON.parse(JSON.stringify(this.currentRepository))

          this.openModal("edit-repository")
        }
      },
    },
    "filterList.currentPage": {
      deep: true,
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$emit("onUpdateCurrentPage", this.filterList)
        }
      },
    },
  },
  methods: {
    clickCreateRepository() {
      this.clearRepository()
      this.openModal("create-repository")
    },
    clickButtonSearch() {
      this.$emit("onUpdateFilterData", this.filterList)
    },
    clickButtonClearFilter() {
      if (this.currentVeeamId) {
        this.$emit("onSetDefaultFilterData")
      }
    },
    selectRow(row) {
      this.repository = {
        name: row.repository.name,
        server: row.repository.ip,
        description: row.description,
        path: row.path,
        limitTask: row.repository.limitTask,
        readWriteRate: row.repository.readWriteRate,
        mountServer: this.mountServer.mountServerId,
        cacheRecoveryPath: this.mountServer.writeCacheFolder,
      }

      this.$emit("selectRow", row.id)
    },
    clickDeleteRepository(row) {
      this.repository = row.item
      this.deleteMessage.repository = row.item.repository.name
      this.deleteMessage.serverIp = row.item.repository.ip

      this.openModal("delete-repository")
    },
    clickConfirmCreateRepository() {
      const createRepository = {
        name: this.repository.name,
        description: this.repository.description,
        type: this.repository.server.type,
        hostId: this.repository.server.id,
        repository: {
          path: this.repository.path,
          maxTaskCount: this.repository.limitTask,
          readWriteRate: this.repository.readWriteRate,
          advancedSettings: {
            alignDataBlocks: true,
            decompressBeforeStoring: false,
            rotatedDrives: false,
            perVmBackup: false,
          },
        },
        mountServer: this.mountServer,
      }

      this.isValidateTrigger = true

      if (!this.isError) {
        this.$emit("createRepository", createRepository)

        this.closeModal()
      }
    },
    clickCancelCreateRepository() {
      this.clearRepository()
    },
    clickConfirmEditRepository() {
      const updateRepository = {
        name: this.repository.name,
        description: this.repository.description,
        type: this.repository.type,
        hostId: this.repository.hostId,
        repository: {
          path: this.repository.path,
          maxTaskCount: this.repository.limitTask,
          readWriteRate: this.repository.readWriteRate,
          advancedSettings: this.repository.advancedSettings,
        },
        mountServer: this.repository.mountServer,
      }

      this.isValidateTrigger = true

      if (!this.isError) {
        this.$emit("updateRepository", this.repository.id, updateRepository)

        this.closeModal()

        this.clearRepository()
      }
    },
    clickCancelEditRepository() {
      this.repository = JSON.parse(JSON.stringify(this.currentRepository))
    },
    confirmDelete() {
      this.$emit("deleteRepository", this.repository.id)

      this.closeModal()
    },
    cancelDelete() {
      this.closeModal()
    },
    openModal(id) {
      if (this.modalId) this.closeModal()
      this.modalId = id
      this.$bvModal.show(this.modalId)
    },
    // close modal
    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = null
      this.isValidateTrigger = false
    },
    clearRepository() {
      this.repository = {
        id: "",
        name: "",
        server: "",
        description: "",
        path: "",
        limitTask: 4,
        readWriteRate: 0,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.organism-repository_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.card-padding {
  padding: 1.5rem;
}
.card-body {
  padding: 0;
}
.filter-container {
  border-bottom: 1px solid #e9ecef;
}

.search__button {
  margin-top: 23px;
  margin-right: 0;
  height: fit-content;
}

.header__label {
  font-weight: 500;
  font-size: 18px;
  color: #5e5873;
}

.sub-header {
  font-weight: 400;
  font-size: 12px;
  color: #b9b9c3;
}

.row-in-modal {
  padding: 0px;
  margin: 0px;
}

.width-85px {
  width: 85px;
}

.empty-data__icon {
  width: 100px;
  height: 100px;
}

.empty-data__text--title {
  color: #5e5873;
  font-weight: 500;
  font-size: 18px;
  margin-top: 20px;
}

.empty-data__text--body {
  color: #b9b9c3;
  font-size: 14px;
  margin-top: 10px;
}

.reset-filter-button {
  color: #b9b9c3;
  margin-bottom: 8px;
  text-decoration: underline;
  &:not(.disabled-button):hover {
    cursor: pointer;
    color: #7367f0;
  }
}
</style>
